import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";

import { useAuth } from "@/lib/auth";
import { getNotificationsService, markNotificationsReadService } from "@/services/notifications.service";
import { IAPIResponse } from "@/services/api.types";
import {
  MarkReadPayload,
  NotificationsParams,
  NotificationsResponse,
} from "@/types/notifications.types";

export const notificationQueryKeys = {
  all: () => ["notifications"] as const,
  list: (params: NotificationsParams) => [...notificationQueryKeys.all(), "list", params] as const,
};

export const useGetNotifications = (params: NotificationsParams = {}) =>
  useQuery<NotificationsResponse, Error>({
    queryKey: notificationQueryKeys.list(params),
    queryFn: () => getNotificationsService(params),
  });

/** One row, no date range, no filters — the response is fetched for its
 *  `unread_count`, which is computed over the whole feed only when the range and
 *  search are absent. `limit: 1` keeps the payload to a single row. */
const UNREAD_BADGE_PARAMS: NotificationsParams = { page: 1, limit: 1 };

/**
 * Unread total for the header bell and the sidebar nav badge.
 *
 * Polls rather than pushes: the feed is written by other people's requests and
 * by the hourly overdue sweep, so nothing in this tab knows an event landed.
 * Sharing `notificationQueryKeys` with the Notifications page means mark-read
 * invalidates this too, and the badge clears without its own round trip.
 */
export const useNotificationUnreadCount = (): number => {
  const { user } = useAuth();
  const { data } = useQuery<NotificationsResponse, Error>({
    queryKey: notificationQueryKeys.list(UNREAD_BADGE_PARAMS),
    queryFn: () => getNotificationsService(UNREAD_BADGE_PARAMS),
    enabled: Boolean(user),
    refetchInterval: 60_000,
    // Background tabs stop polling; focus refetch covers the gap on return.
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,  // overrides the app-wide default in index.tsx
    staleTime: 30_000,
  });
  return data?.unread_count ?? 0;
};

export const useMarkNotificationsRead = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<{ updated: number }>, AxiosError, MarkReadPayload>({
    mutationFn: markNotificationsReadService,
    onSuccess: res => {
      // Read state changes both the rows and the unread badge, and the badge is
      // derived from the same response — so invalidate the whole family rather
      // than the one filtered page that happened to be on screen.
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all() });
      if (res.data?.updated) toast.success(res.message);
    },
    onError: err => toast.error(err.message),
  });
};
